import { type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router";

import { BetaBadge } from "@/shared/components/beta-badge";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/shared/components/ui/sidebar";
import { cn } from "@/shared/lib/utils";

type NavMainItem = {
  title: string;
  url: string;
  icon?: LucideIcon;
  isBeta?: boolean;
};

type NavMainProps = {
  items: NavMainItem[];
};

export function NavMain({ items }: NavMainProps) {
  const { pathname } = useLocation();

  return (
    <SidebarGroup>
      <SidebarMenu className="gap-1">
        {items.map((item) => {
          const isActive =
            pathname === item.url || pathname.startsWith(`${item.url}/`);

          return (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                isActive={isActive}
                tooltip={item.title}
                className={cn(
                  "group/nav-item relative h-9 rounded-lg px-2.5 font-medium text-sidebar-foreground/75",
                  "transition-colors duration-150",
                  "hover:text-sidebar-foreground",
                  "data-[active=true]:bg-sidebar-primary/8 data-[active=true]:text-sidebar-primary",
                )}
              >
                <Link to={item.url}>
                  {/* Active route indicator bar */}
                  <span
                    aria-hidden
                    className={cn(
                      "absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-r-full bg-sidebar-primary",
                      "origin-left transition-all duration-200",
                      isActive
                        ? "scale-y-100 opacity-100"
                        : "scale-y-0 opacity-0",
                      "group-data-[collapsible=icon]:hidden",
                    )}
                  />
                  {item.icon && (
                    <item.icon
                      className={cn(
                        "transition-transform duration-200 ease-out",
                        "group-hover/nav-item:-translate-y-px group-hover/nav-item:scale-110",
                        isActive && "text-sidebar-primary",
                      )}
                    />
                  )}
                  <span>{item.title}</span>
                  {item.isBeta ? (
                    <BetaBadge className="group-data-[collapsible=icon]:hidden" />
                  ) : null}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
